import {booleanAttribute, Component, computed, input, numberAttribute} from '@angular/core';
import {CdkDragHandle} from '@angular/cdk/drag-drop';
import {NgTemplateOutlet} from '@angular/common';
import {AvatarComponent} from '../avatar/avatar.component';
import {StatusBadgeComponent} from '../status-badge/status-badge.component';
import {Tone} from '../ui-colors';
import {formatDuration} from '../pipes/format';

export type TaskPriority = 'critical' | 'high' | 'normal' | 'low';

/** Status tones of a work item; `dark` is the black "Backlog" tag. */
export type TaskStatusTone = Tone | 'dark';

/** A part of the path after the customer ("Planning fase B › Verrijking"), optionally a link. */
export interface TaskPathPart {
    label: string;
    href?: string | null;
}

export interface TaskOwner {
    name: string;
    seed?: number | string | null;
}

const PRIORITIES: Record<TaskPriority, { stripe: string; last: string; label: string }> = {
    critical: {stripe: '#d93025', last: '#d93025', label: 'Prioriteit kritiek'},
    high: {stripe: '#d93025', last: '#d93025', label: 'Prioriteit hoog'},
    normal: {stripe: '#e0a100', last: '#e0a100', label: 'Prioriteit normaal'},
    low: {stripe: '#4a90d9', last: '#c9ccd1', label: 'Prioriteit laag'},
};

/** Icons of Azure DevOps work item types, in the colors Azure DevOps uses for them; other types get a grey icon. */
const TASK_TYPES: Record<string, { icon: string; tone: Tone }> = {
    'Bug': {icon: 'fa-bug', tone: 'error'},
    'User Story': {icon: 'fa-book-open', tone: 'info'},
    'Task': {icon: 'fa-clipboard-check', tone: 'warn'},
    'Feature': {icon: 'fa-trophy', tone: 'test'},
};
const OTHER_TASK_TYPE = {icon: 'fa-file', tone: 'neutral' as Tone};

/** Azure DevOps work item states with the tone of their tag and the dot color of their filter chip. */
export const TASK_STATES: Record<string, { tone: TaskStatusTone; dot: string }> = {
    'Te testen': {tone: 'test', dot: '#8b6ee6'},
    'Active': {tone: 'ok', dot: '#3fb97a'},
    'Resolved': {tone: 'warn', dot: '#e0a100'},
    'New': {tone: 'info', dot: '#6b8afd'},
    'Backlog': {tone: 'dark', dot: '#2b2d30'},
    'On hold': {tone: 'neutral', dot: '#b0b4ba'},
    'Closed': {tone: 'neutral', dot: '#b0b4ba'},
    'Done': {tone: 'neutral', dot: '#b0b4ba'},
    'Removed': {tone: 'neutral', dot: '#b0b4ba'},
};

export function taskStatusTone(state: string | null | undefined): TaskStatusTone {
    return TASK_STATES[state ?? '']?.tone ?? 'neutral';
}

export function taskStatusDot(state: string | null | undefined): string {
    return TASK_STATES[state ?? '']?.dot ?? '#b0b4ba';
}

/**
 * An Azure DevOps work item. `row` is the list row of the daily (priority stripes, number + parent,
 * type icon + title, customer › path, PR icon, status tag with the hours below it); `card` is the kanban card
 * with the owner avatar.
 * Priority `critical` also colors the whole row red, so a critical bug stands out from a high one.
 * Presentation only: selection, context menus and drag & drop are bound by the page on the host
 * (`dragHandle` turns the priority stripes into the `cdkDragHandle` of a `cdkDrag` on the host).
 */
@Component({
    selector: 'app-task-row',
    imports: [CdkDragHandle, NgTemplateOutlet, AvatarComponent, StatusBadgeComponent],
    templateUrl: './task-row.component.html',
    styleUrl: './task-row.component.scss',
    host: {
        '[class]': '"is-" + variant() + " size-" + size()',
        '[class.with-changed]': 'showChanged()',
        '[class.selected]': 'selected()',
        '[class.highlighted]': 'highlighted()',
        '[class.critical]': 'priority() === "critical"',
        '[style.--prio]': 'priorityStyle().stripe',
        '[style.--prio-last]': 'priorityStyle().last',
        '[attr.title]': 'null'
    }
})
export class TaskRowComponent {
    readonly variant = input<'row' | 'card'>('row');
    readonly size = input<'md' | 'sm'>('md');
    readonly id = input.required<number | string>();
    readonly parentId = input<number | string | null>(null);
    readonly parentTitle = input<string | null>(null);
    readonly title = input<string>('');
    /** Work item type (Bug, User Story, Task, Feature), shown as a colored icon before the title. */
    readonly type = input<string | null>(null);
    /** Link of the work item (number and title); the parent number links to `parentHref`. */
    readonly href = input<string | null>(null);
    readonly parentHref = input<string | null>(null);
    readonly client = input<string>('');
    readonly path = input<(TaskPathPart | string | null | undefined)[]>([]);
    /** Text color of the customer, e.g. `companyId | customerColor:'ink'`; neutral when not given. */
    readonly clientColor = input<string | null>(null);
    readonly status = input<string>('');
    readonly statusTone = input<TaskStatusTone>('neutral');
    readonly priority = input<TaskPriority>('normal');
    /** Tooltip of the priority stripes; defaults to "Prioriteit kritiek/hoog/normaal/laag". */
    readonly priorityLabel = input<string | null>(null);
    readonly prCount = input(0, {transform: numberAttribute});
    readonly showChanged = input(false, {transform: booleanAttribute});
    readonly changedBy = input<string>('');
    readonly changedWhen = input<string>('');
    /** Hours from the Urenbesteding and Urenindicatie fields, shown as "besteed / indicatie" when either is set. */
    readonly hoursSpent = input<number | null>(null);
    readonly hoursEstimate = input<number | null>(null);
    /** Card only: avatar of the person the work item is assigned to. */
    readonly owner = input<TaskOwner | null>(null);
    readonly selected = input(false, {transform: booleanAttribute});
    readonly highlighted = input(false, {transform: booleanAttribute});
    readonly dragHandle = input(false, {transform: booleanAttribute});

    readonly priorityStyle = computed(() => PRIORITIES[this.priority()] ?? PRIORITIES.normal);
    readonly priorityTitle = computed(() => this.priorityLabel() ?? this.priorityStyle().label);
    readonly hours = computed(() => {
        const spent = this.hoursSpent() ?? 0;
        const estimate = this.hoursEstimate() ?? 0;
        if (!spent && !estimate) {
            return null;
        }
        const format = (hours: number) => formatDuration(Math.round(hours * 60));
        return {
            label: `${format(spent)} / ${estimate ? format(estimate) : '–'}`,
            title: `Urenbesteding ${format(spent)} · urenindicatie ${estimate ? format(estimate) : 'niet ingevuld'}`,
            over: !!estimate && spent > estimate,
        };
    });
    readonly typeStyle = computed(() => this.type() ? TASK_TYPES[this.type()] ?? OTHER_TASK_TYPE : null);
    readonly pathParts = computed<TaskPathPart[]>(() => this.path()
        .filter(part => !!part && (typeof part === 'string' || !!part.label))
        .map(part => typeof part === 'string' ? {label: part} : part));
}
